import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Download, Trash2, MailCheck } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { formatDate } from "@/lib/format";

const L = {
  it: {
    total: "Iscritti", confirmed: "Confermati", pending: "In attesa", unsub: "Cancellati",
    composer: "Nuova newsletter", subject: "Oggetto (IT)", subjectEn: "Oggetto (EN)",
    body: "Messaggio (IT)", bodyEn: "Messaggio (EN)",
    subjectPh: "Es. Nuovo articolo: Incoterms in pratica",
    bodyPh: "Ciao, questo mese abbiamo pubblicato…",
    template: "Usa modello",
    hint: "Sotto il messaggio aggiungiamo automaticamente i 3 articoli più recenti e il link per cancellarsi. Ogni iscritto riceve la versione nella sua lingua (se scritta); saluto e testi fissi seguono la lingua scelta all'iscrizione.",
    send: "Invia agli iscritti", sending: "Invio in corso…",
    confirm: (n) => `Inviare la newsletter a ${n} iscritti confermati?`,
    queued: "Newsletter in invio (arriverà nelle caselle entro pochi minuti)",
    empty: "Nessun iscritto per ora: il form nel footer del sito raccoglie le iscrizioni.",
    history: "Invii precedenti", noHistory: "Nessun invio effettuato.",
    subscribers: "Elenco iscritti", export: "Esporta CSV",
    stConfirmed: "confermato", stPending: "in attesa", stUnsub: "cancellato",
    del: "Elimina", deleted: "Iscritto eliminato", delConfirm: (e) => `Eliminare l'iscritto ${e}?`, sourceGuide: "Guida",
    testBtn: "Email di prova", testQueued: "Email di prova inviata: versione IT e EN nella tua casella di posta", testNeedsBody: "Compila oggetto e messaggio (IT) prima di inviare la prova"
  },
  en: {
    total: "Subscribers", confirmed: "Confirmed", pending: "Pending", unsub: "Unsubscribed",
    composer: "New newsletter", subject: "Subject (IT)", subjectEn: "Subject (EN)",
    body: "Message (IT)", bodyEn: "Message (EN)",
    subjectPh: "E.g. New article: Incoterms in practice",
    bodyPh: "Hi, this month we published…",
    template: "Use template",
    hint: "Below the message we automatically add the 3 latest articles and the unsubscribe link. Each subscriber receives the version in their language (if written); greetings and fixed texts follow the language chosen at signup.",
    send: "Send to subscribers", sending: "Sending…",
    confirm: (n) => `Send the newsletter to ${n} confirmed subscribers?`,
    queued: "Newsletter queued (it will reach inboxes within minutes)",
    empty: "No subscribers yet: the form in the site footer collects subscriptions.",
    history: "Past sends", noHistory: "No newsletter sent yet.",
    subscribers: "Subscriber list", export: "Export CSV",
    stConfirmed: "confirmed", stPending: "pending", stUnsub: "unsubscribed",
    del: "Delete", deleted: "Subscriber removed", delConfirm: (e) => `Remove subscriber ${e}?`, sourceGuide: "Guide",
    testBtn: "Test email", testQueued: "Test email sent: IT and EN versions in your inbox", testNeedsBody: "Fill in the (IT) subject and message before sending the test"
  }
};

const STATUS_KEY = { confirmed: "stConfirmed", pending: "stPending", unsubscribed: "stUnsub" };
const STATUS_CLS = {
  confirmed: "border-gold/40 bg-gold/15 text-gold-bright",
  pending: "border-white/15 bg-white/5 text-ivory/50",
  unsubscribed: "border-red-400/30 bg-red-500/10 text-red-300"
};

const TPL = {
  subject: "Nuovi articoli su Incoterms, dogane e logistica",
  subject_en: "New articles on Incoterms, customs and logistics",
  body: "Ciao,\n\nquesto mese abbiamo arricchito il Knowledge Center con nuovi approfondimenti pensati per chi esporta e importa: termini di consegna, adempimenti doganali e gestione delle spedizioni.\n\nTi segnaliamo anche il nuovo insights sulla scelta tra FCA e FOB: un tema che, nelle spedizioni in container, fa una differenza concreta su costi e responsabilità.\n\nBuona lettura,\nLB Trade Advisory",
  body_en: "Hi,\n\nthis month we expanded the Knowledge Center with new insights for exporters and importers: delivery terms, customs requirements and shipment management.\n\nWe also published a new analysis on choosing between FCA and FOB: a topic that makes a real difference on costs and liabilities in containerized shipments.\n\nEnjoy the read,\nLB Trade Advisory"
};

export default function NewsletterPanel() {
  const { lang } = useLang();
  const a = L[lang];
  const [data, setData] = useState(undefined);
  const [history, setHistory] = useState([]);
  const [subject, setSubject] = useState("");
  const [subjectEn, setSubjectEn] = useState("");
  const [body, setBody] = useState("");
  const [bodyEn, setBodyEn] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/newsletter").then((r) => setData(r.data)).catch(() => setData({ subscribers: [], confirmed: 0, total: 0 }));
    api.get("/admin/newsletter/sent").then((r) => setHistory(r.data || [])).catch(() => {});
  };

  useEffect(load, []);

  const removeSubscriber = async (s) => {
    if (!window.confirm(a.delConfirm(s.email))) return;
    try {
      await api.delete(`/admin/newsletter/subscribers/${s.id}`);
      toast.success(a.deleted);
      load();
    } catch {
      toast.error("Error");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/newsletter/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "newsletter-lbtradeadvisory.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export non riuscito");
    }
  };

  const send = async () => {
    if (!data || !data.confirmed) {
      toast.error(a.empty);
      return;
    }
    if (!window.confirm(a.confirm(data.confirmed))) return;
    setBusy(true);
    try {
      await api.post(
        "/admin/newsletter/send",
        {
          subject: subject.trim(),
          body: body.trim(),
          subject_en: subjectEn.trim() || null,
          body_en: bodyEn.trim() || null,
        },
        { timeout: 30000 }
      );
      toast.success(a.queued);
      setSubject("");
      setSubjectEn("");
      setBody("");
      setBodyEn("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error(a.testNeedsBody);
      return;
    }
    setBusy(true);
    try {
      await api.post(
        "/admin/newsletter/test-send",
        {
          subject: subject.trim(),
          body: body.trim(),
          subject_en: subjectEn.trim() || null,
          body_en: bodyEn.trim() || null,
        },
        { timeout: 30000 }
      );
      toast.success(a.testQueued);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  if (data === undefined) {
    return <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-ivory/40">…</p>;
  }

  const inputCls =
    "w-full border border-white/15 bg-white/5 px-4 py-3 text-sm text-ivory placeholder:text-ivory/30 transition-colors focus:border-gold/60 focus:outline-none";
  const chips = [
    { label: a.total, value: data.total },
    { label: a.confirmed, value: data.confirmed },
    { label: a.pending, value: (data.subscribers || []).filter((s) => s.status === "pending").length },
    { label: a.unsub, value: (data.subscribers || []).filter((s) => s.status === "unsubscribed").length },
  ];

  return (
    <div data-testid="admin-newsletter-panel">
      <div className="grid gap-4 sm:grid-cols-4">
        {chips.map((c) => (
          <div key={c.label} className="border border-white/10 bg-white/[0.02] p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/50">{c.label}</p>
            <p className="mt-2 font-serif text-3xl text-ivory">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 border border-gold/20 bg-navy-light p-6 lg:p-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{a.composer}</p>
        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          <div>
            <label htmlFor="nl-subject" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{a.subject}</label>
            <input
              id="nl-subject"
              data-testid="newsletter-subject-input"
              className={inputCls}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={a.subjectPh}
            />
          </div>
          <div>
            <label htmlFor="nl-subject-en" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{a.subjectEn}</label>
            <input
              id="nl-subject-en"
              data-testid="newsletter-subject-en-input"
              className={inputCls}
              value={subjectEn}
              onChange={(e) => setSubjectEn(e.target.value)}
              placeholder={a.subjectPh}
            />
          </div>
          <div>
            <label htmlFor="nl-body" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{a.body}</label>
            <textarea
              id="nl-body"
              data-testid="newsletter-body-input"
              rows={7}
              className={`${inputCls} resize-y`}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={a.bodyPh}
            />
          </div>
          <div>
            <label htmlFor="nl-body-en" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{a.bodyEn}</label>
            <textarea
              id="nl-body-en"
              data-testid="newsletter-body-en-input"
              rows={7}
              className={`${inputCls} resize-y`}
              value={bodyEn}
              onChange={(e) => setBodyEn(e.target.value)}
              placeholder={a.bodyPh}
            />
          </div>
        </div>
        <div className="mt-4">
          <p className="text-[11px] leading-relaxed text-ivory/35">
            {lang === "it"
              ? "Sotto il messaggio aggiungiamo automaticamente i 3 articoli più recenti e il link per cancellarsi. Ogni iscritto riceve la versione nella sua lingua (se scritta); saluto e testi fissi seguono la lingua scelta all'iscrizione."
              : "Below the message we automatically add the 3 latest articles and the unsubscribe link. Each subscriber receives the version in their language (if written); greetings and fixed texts follow the language chosen at signup."}
          </p>
        </div>
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={send}
              data-testid="newsletter-send-button"
              disabled={busy || !data.confirmed}
              className="inline-flex items-center gap-2 bg-gold px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-gold-bright disabled:pointer-events-none disabled:opacity-50"
            >
              <Send className="h-4 w-4" /> {busy ? a.sending : `${a.send} (${data.confirmed})`}
            </button>
            <button
              onClick={() => {
                setSubject(TPL.subject);
                setSubjectEn(TPL.subject_en);
                setBody(TPL.body);
                setBodyEn(TPL.body_en);
              }}
              data-testid="newsletter-template-button"
              className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-ivory/60 transition-colors hover:border-gold/50 hover:text-gold-bright"
            >
              {a.template}
            </button>
            <button
              onClick={sendTest}
              data-testid="newsletter-test-send-button"
              disabled={busy || !subject.trim() || !body.trim()}
              className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-ivory/60 transition-colors hover:border-gold/50 hover:text-gold-bright disabled:pointer-events-none disabled:opacity-50"
            >
              <MailCheck className="h-4 w-4" /> {a.testBtn}
            </button>
          </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{a.history}</p>
        </div>
        {history.length === 0 ? (
          <p className="mt-4 text-sm text-ivory/40">{a.noHistory}</p>
        ) : (
          <div className="mt-4 space-y-2">
            {history.map((h, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-white/[0.02] px-4 py-3">
                <span className="font-serif text-base text-ivory">{h.subject}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ivory/40">
                  {formatDate(h.sent_at, lang)} · {h.sent} ✓ {h.errors ? `· ${h.errors} ✗` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{a.subscribers}</p>
          <button
            onClick={exportCsv}
            data-testid="newsletter-export-button"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ivory/60 transition-colors hover:border-gold/50 hover:text-gold-bright"
          >
            <Download className="h-3 w-3" /> {a.export}
          </button>
        </div>
        {data.total === 0 ? (
          <p className="mt-4 border border-gold/15 py-12 text-center text-sm text-ivory/50" data-testid="newsletter-empty">{a.empty}</p>
        ) : (
          <div className="mt-4" data-testid="newsletter-subscribers-list">
            {(data.subscribers || []).map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-gold/10 py-3 last:border-b">
                <div>
                  <p className="font-mono text-sm text-ivory">{s.email}</p>
                  {s.name && <p className="text-xs text-ivory/40">{s.name}</p>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[10px] text-ivory/30">{formatDate(s.created_at, lang)}</span>
                  <span className={`border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_CLS[s.status]}`}>
                    {a[STATUS_KEY[s.status]] || s.status}
                  </span>
                  {s.source === "guide" && (
                    <span className="border border-gold/40 bg-gold/10 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-gold-bright" data-testid="subscriber-source-guide">
                      {a.sourceGuide}
                    </span>
                  )}
                  <button
                    onClick={() => removeSubscriber(s)}
                    data-testid={`newsletter-delete-subscriber-${s.id}`}
                    title={a.del}
                    aria-label={a.del}
                    className="border border-white/15 p-1.5 text-ivory/40 transition-colors hover:border-red-400/60 hover:text-red-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
